import {Checkbox, IconButton, openDeleteDialog, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Space, Text, VStack} from 'platform/foundation';

import {Fragment} from 'react';

import {isNil, not} from 'ramda';

import {MappedBasketItem, TITLE_TOOLTIP_OPEN_DELAY} from '@dms/shared';

import {EMPTY_PLACEHOLDER, getRandomId, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CorrectionBasketItem} from '../types/CorrectionBasketItem';
import {CorrectionBasketItemEditingDetails} from '../types/CorrectionBasketItemEditingDetails';

interface AfterSalesCorrectionBasketItemTitleWithActionsProps<T extends CorrectionBasketItem>
  extends RequiredTestIdProps {
  item: MappedBasketItem<T>;
  onSelect: (itemId: string, isSelected: boolean) => void;
  onEdit: (editingDetails: CorrectionBasketItemEditingDetails) => void;
  onDelete: (itemsIds: string[]) => Promise<void>;
  hasPendingRequest?: boolean;
}

export function AfterSalesCorrectionBasketItemTitleWithActions<T extends CorrectionBasketItem>(
  props: AfterSalesCorrectionBasketItemTitleWithActionsProps<T>
) {
  const handleEditItem = () => {
    if (isNil(props.item.id)) {
      throw new Error("Missing item's id");
    }

    props.onEdit({
      itemId: props.item.id,
      itemName: props.item.name,
    });
  };

  const handleDeleteItem = () => {
    openDeleteDialog({
      name: props.item.name,
      onConfirm: () => props.onDelete([props.item.id]),
      'data-testid': suffixTestId('delete', props),
    });
  };

  const tooltip = props.item.tooltip
    ? props.item.tooltip.map((item, index) => (
        <Fragment key={item.label}>
          {index > 0 && <Space vertical={2} />}
          <Text size="xSmall" alternative color="white">
            {item.label}
          </Text>
          <Text size="xSmall" color="white">
            {item.value || EMPTY_PLACEHOLDER}
          </Text>
        </Fragment>
      ))
    : null;

  return (
    <VStack spacing={1}>
      <HStack align="center" spacing={2}>
        <Checkbox
          isDisabled={props.hasPendingRequest}
          value={props.item.isSelected}
          onChange={(isSelected) => props.onSelect(props.item.id, isSelected)}
          data-testid={suffixTestId('isSelected', props)}
        />
        <Box flexGrow={1} minWidth={0}>
          <Tooltip
            label={props.item?.number ?? props.item?.manufacturerNumber}
            placement="top"
            openDelay={TITLE_TOOLTIP_OPEN_DELAY}
            hasAutoWidth
          >
            <Heading size={4} isSingleLine data-testid={suffixTestId('number', props)}>
              {props.item?.number ?? props.item?.manufacturerNumber}
            </Heading>
          </Tooltip>
        </Box>
        <HStack justify="flex-end" shrink={0} spacing={1}>
          {tooltip && (
            <Tooltip
              key={`basketItemTooltip-${getRandomId()}`}
              placement="top"
              description={tooltip}
            >
              <IconButton
                icon="action/info_outline"
                size="small"
                priority="secondary"
                data-testid={suffixTestId('tooltip', props)}
              />
            </Tooltip>
          )}
          <IconButton
            isDisabled={props.hasPendingRequest || not(props.item.itemEditingAllowed)}
            icon="image/edit"
            size="small"
            priority="secondary"
            onClick={handleEditItem}
            data-testid={suffixTestId('edit', props)}
          />
          <IconButton
            isDisabled={props.hasPendingRequest}
            icon="navigation/close"
            size="small"
            priority="primary"
            severity="danger"
            onClick={handleDeleteItem}
            data-testid={suffixTestId('delete', props)}
          />
        </HStack>
      </HStack>
      <Text size="xSmall" color="tertiary" data-testid={suffixTestId('name', props)}>
        {props.item.name}
      </Text>
      <Space vertical={2} />
    </VStack>
  );
}
